import api from "./api";
import useUserStore from "../stores/userStore";

/**
 * Waits for the user store to be initialized and returns userId and communityId.
 * @returns {Promise<{userId: string, communityId: string}>} Resolves with userId and communityId.
 */
const waitForState = async () => {
  //used to wait for state since userStore in initialzed on app loading
  return new Promise((resolve) => {
    const checkState = () => {
      const { userId, communityId } = useUserStore.getState();
      if (userId && communityId) {
        resolve({ userId, communityId });
      } else {
        setTimeout(checkState, 100);
      }
    };
    checkState();
  });
};

/**
 * Maps a group API item to a group data object.
 * @param {object} item - The group item from the API.
 * @returns {object} The mapped group data.
 */
export const groupDataMap = (item) => ({
  groupId: item?._id,
  groupName: item?.name,
  communityName: item?.community?.name || "N/A",
  communityId: item?.community?._id || "N/A",
  status:
    item?.status === "A"
      ? "Active"
      : item?.status === "D"
      ? "Deactivated"
      : item?.status,
  groupLeader:
    item?.leaders && item?.leaders.length > 0
      ? item?.leaders
          .map((leader) => leader.profile?.name)
          .filter(Boolean) // remove any undefined/null names
          .join(", ")
      : "N/A",
  totalMembers: item?.total,
  dateCreated: item?.create_date
    ? new Date(item.create_date).toLocaleDateString()
    : "N/A",
});

export const groupService = {
  /**
   * Fetches groups for a given community or group.
   * @param {string|null} communityId - The community ID to filter groups (optional).
   * @param {string|null} groupId - The group ID to filter groups (optional).
   * @returns {Promise<object[]>} The list of groups.
   */
  getGroups: async (communityId = null, groupId = null) => {
    try {
      const { userId } = await waitForState(); // Only get userId

      // Build base URL
      let url = `/mapi/group/getGroup?EntityId=${userId}`;

      // Add CommunityId only if provided
      if (communityId) url += `&CommunityId=${communityId}`;
      if (groupId) url += `&GroupId=${groupId}`;

      const response = await api.get(url);

      console.log("groupService getGroups response.data: ", response.data);
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching group list data:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  getGroupById: async (groupId) => {
    try {
      const { userId } = await waitForState(); // Only get userId

      if (!groupId) {
        throw new Error("GroupId is required");
      }

      // Build URL
      const url = `/mapi/group/getGroupById?EntityId=${userId}&GroupId=${groupId}`;

      const response = await api.get(url);

      console.log("groupService getGroupById response.data: ", response.data);
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching group data by ID:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Edits a group's details.
   * @param {object} groupData - The group data to update.
   * @returns {Promise<object>} The updated group data.
   */
  editGroup: async (groupData) => {
    try {
      console.log("Editing Group:", groupData);

      const response = await api.post(`/mapi/group/editGroup`, groupData);
      return response.data;
    } catch (error) {
      console.error(
        "Error updating group details:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Adds a new group with emails.
   * @param {object} groupData - The group data including emails.
   * @returns {Promise<object>} The created group data.
   */
  addGroupWithEmails: async (groupData) => {
    try {
      console.log("Creating Group with Emails:", groupData);
      const response = await api.post(
        "/mapi/group/addGroupWithEmails",
        groupData
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error creating group with emails:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Removes a group by its ID.
   * @param {string} groupId - The ID of the group to remove.
   * @returns {Promise<object>} The result of the removal operation.
   */
  removeGroup: async (groupId) => {
    try {
      const { userId } = await waitForState();
      const payload = {
        OperatorId: userId,
        GroupId: groupId,
      };

      console.log("Removing Group:", payload);

      const response = await api.post("/mapi/group/removeGroup", payload);
      return response.data;
    } catch (error) {
      console.error(
        "Error removing group:",
        error.response?.data || error.message
      );
      throw error;
    }
  },
};

export default groupService;
